import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, TextField, Select, MenuItem,
  IconButton, InputAdornment,
} from '@mui/material';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import ZmanSelect from '../components/ZmanSelect';
import { getZmanLabel } from '../components/zmanDefinitions';

const navy = '#1e3a5f';

const PRAYER_TYPES = [
  { value: 'shacharit', label: 'שחרית' },
  { value: 'musaf',     label: 'מוסף'  },
  { value: 'mincha',    label: 'מנחה'  },
  { value: 'maariv',   label: 'מעריב' },
  { value: 'other',    label: 'אחר'   },
];

// בסיסי זמן נוספים הרלוונטיים רק למועד
const MOAD_EXTRA_ZMAN_OPTIONS = [
  { key: 'dayEntryTime',   label: 'כניסת המועד'         },
  { key: 'dayExitTime',    label: 'יציאת המועד'         },
  { key: 'dayEntrySunset', label: 'שקיעה בכניסת המועד' },
  { key: 'dayExitSunset',  label: 'שקיעה ביציאת המועד' },
];
// בסיסי זמן נוספים הרלוונטיים לשבת
const SHABBAT_EXTRA_ZMAN_OPTIONS = [
  // sentinel — לפי הגדרת כניסה/יציאה גלובלית
  { key: 'dayEntryTime',      label: 'כניסת שבת — לפי ההגדרה' },
  { key: 'dayExitTime',       label: 'צאת שבת — לפי ההגדרה' },
  // לוח שנה
  { key: 'candleLighting',    label: 'הדלקת נרות (לוח שנה)' },
  { key: 'havdalah',          label: 'הבדלה (לוח שנה)' },
  { key: 'shabbatHavdalah',   label: 'צאת שבת (הבדלה)' },
  { key: 'dayHavdalah',       label: 'הבדלה' },
  // יום שישי
  { key: 'fridaySunset',      label: 'שקיעה — שישי' },
  { key: 'fridayTzeit85',     label: 'צאת כוכבים — שישי' },
  { key: 'fridayTzeit7083',   label: 'צאת ר״ת — שישי' },
  { key: 'fridayTzeit42',     label: 'צאת 42 דק׳ — שישי' },
  { key: 'fridayTzeit50',     label: 'צאת 50 דק׳ — שישי' },
  { key: 'fridayTzeit72',     label: 'צאת 72 דק׳ — שישי' },
  // שבת
  { key: 'shabbatSunset',     label: 'שקיעה — שבת' },
  { key: 'shabbatTzeit85',    label: 'צאת כוכבים — שבת' },
  { key: 'shabbatTzeit7083',  label: 'צאת ר״ת — שבת' },
  { key: 'shabbatTzeit42',    label: 'צאת 42 דק׳ — שבת' },
  { key: 'shabbatTzeit50',    label: 'צאת 50 דק׳ — שבת' },
  { key: 'shabbatTzeit72',    label: 'צאת 72 דק׳ — שבת' },
  // מפתחות ישנים
  { key: 'sofZmanShma',       label: 'סוף זמן שמע' },
  { key: 'sofZmanShmaMga',    label: 'סוף זמן שמע מג"א' },
];

const TIME_TYPES = [
  { value: 'fixed',     label: 'שעה קבועה',        desc: 'שעה ספציפית שאינה משתנה' },
  { value: 'relative',  label: 'יחסי לזמן הלכתי',  desc: 'לפני/אחרי זמן הלכתי ידוע' },
  { value: 'recurring', label: 'חוזר בטווח',        desc: 'כל X דקות בין שעה א׳ לשעה ב׳' },
];

// ימי שבוע — ל-tefilot / shabbat
const WEEKDAYS = [
  { key: 'sun', label: 'א׳' },
  { key: 'mon', label: 'ב׳' },
  { key: 'tue', label: 'ג׳' },
  { key: 'wed', label: 'ד׳' },
  { key: 'thu', label: 'ה׳' },
  { key: 'fri', label: 'ו׳' },
  { key: 'sat', label: 'ש׳' },
];
// לתפילות יום חול — שבת לא רלוונטית
const TEFILOT_WEEKDAYS = WEEKDAYS.filter(d => d.key !== 'sat');
const TEFILOT_WEEKDAY_KEYS = TEFILOT_WEEKDAYS.map(d => d.key);
const ALL_WEEKDAYS = WEEKDAYS.map(d => d.key);

const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };
const FIELD_MAP = { tefilot: 'customTimes', shabbat: 'shabbatPrayers', moad: 'moadPrayers' };

// ─── DayChip ──────────────────────────────────────────────────────────────────

const DayChip = ({ label, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      width: 36, height: 36,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: '50%', cursor: 'pointer', transition: 'all 0.15s',
      fontWeight: selected ? 700 : 400,
      fontSize: '0.82rem',
      bgcolor: selected ? navy : 'white',
      color: selected ? 'white' : '#6b7280',
      border: `1.5px solid ${selected ? navy : '#e5e7eb'}`,
      '&:hover': { borderColor: navy, color: selected ? 'white' : navy },
    }}
  >
    {label}
  </Box>
);

// ─── SubStep4 — ימים ──────────────────────────────────────────────────────────

const SubStep4 = ({ draft, setDraft, mode, moadDays }) => {
  if (mode === 'moad' && moadDays?.length > 0) {
    // מועד: בחירה לפי ימי המועד שהוגדרו
    const selected = draft.moadDayIndices || [];
    const toggle = (i) => {
      const next = selected.includes(i) ? selected.filter(x => x !== i) : [...selected, i];
      setDraft(d => ({ ...d, moadDayIndices: next }));
    };
    return (
      <Box>
        <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 600, mb: 1 }}>
          לאיזה ימי המועד שייכת התפילה?
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mb: 1.5, lineHeight: 1.5 }}>
          בחר את הימים שבהם התפילה מתקיימת.
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1} mb={1.5}>
          {moadDays.map((day, i) => (
            <Box
              key={i}
              onClick={() => toggle(i)}
              sx={{
                px: 1.5, py: 0.6, borderRadius: '20px', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: selected.includes(i) ? 700 : 400,
                bgcolor: selected.includes(i) ? navy : 'white',
                color: selected.includes(i) ? 'white' : '#374151',
                border: `1.5px solid ${selected.includes(i) ? navy : '#e5e7eb'}`,
                transition: 'all 0.15s',
              }}
            >
              {day.name}
            </Box>
          ))}
        </Stack>
        {selected.length === 0 && (
          <Typography sx={{ fontSize: '0.72rem', color: '#ef4444', mt: 0.5 }}>
            יש לבחור לפחות יום אחד
          </Typography>
        )}
      </Box>
    );
  }

  if (mode === 'shabbat') {
    const shabbatDayOptions = [
      { value: 'friday', label: 'יום שישי' },
      { value: 'shabbat', label: 'שבת' },
    ];
    const selectedDay = draft.day || 'shabbat';
    return (
      <Box>
        <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 600, mb: 1 }}>
          מתי מתקיימת התפילה?
        </Typography>
        <Stack direction="row" spacing={1}>
          {shabbatDayOptions.map(opt => {
            const isSelected = selectedDay === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => setDraft(d => ({ ...d, day: opt.value }))}
                sx={{
                  flex: 1, p: 1.5, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s',
                  border: `1.5px solid ${isSelected ? navy : '#e5e7eb'}`,
                  bgcolor: isSelected ? '#f0f4fa' : 'white',
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: isSelected ? navy : '#374151' }}>
                  {opt.label}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  }

  // tefilot — ימי שבוע (ללא שבת)
  const selected = (draft.days || TEFILOT_WEEKDAY_KEYS).filter(k => k !== 'sat');
  const toggleDay = (key) => {
    const next = selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key];
    setDraft(d => ({ ...d, days: next.length > 0 ? next : selected }));
  };
  const allSelected = TEFILOT_WEEKDAYS.every(d => selected.includes(d.key));

  return (
    <Box>
      <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 600, mb: 0.5 }}>
        באילו ימים מתקיימת התפילה?
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 1.5, lineHeight: 1.55 }}>
        בחר את הימים שבהם תפילה זו מתקיימת. השעה שתגדיר בשלב הבא תחול על <strong>כל הימים</strong> שתבחר כאן.
      </Typography>

      <Stack direction="row" spacing={0.75} mb={1.5} flexWrap="wrap">
        {TEFILOT_WEEKDAYS.map(d => (
          <DayChip
            key={d.key}
            label={d.label}
            selected={selected.includes(d.key)}
            onClick={() => toggleDay(d.key)}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={1} mb={2}>
        <Button size="small"
          onClick={() => setDraft(d => ({ ...d, days: TEFILOT_WEEKDAY_KEYS }))}
          disabled={allSelected}
          sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#6b7280', p: 0, minWidth: 0 }}>
          כל הימים
        </Button>
        <Typography sx={{ color: '#e5e7eb', fontSize: '0.75rem', lineHeight: '1.6rem' }}>|</Typography>
        <Button size="small"
          onClick={() => setDraft(d => ({ ...d, days: ['sun','mon','tue','wed','thu','fri'] }))}
          sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#6b7280', p: 0, minWidth: 0 }}>
          א׳–ו׳
        </Button>
        <Typography sx={{ color: '#e5e7eb', fontSize: '0.75rem', lineHeight: '1.6rem' }}>|</Typography>
        <Button size="small"
          onClick={() => setDraft(d => ({ ...d, days: ['sun','mon','tue','wed','thu'] }))}
          sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#6b7280', p: 0, minWidth: 0 }}>
          א׳–ה׳
        </Button>
      </Stack>

      <Box sx={{ bgcolor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 1.5, px: 1.75, py: 1.25 }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#0c4a6e', lineHeight: 1.65 }}>
          <strong>💡 עקיפה לפי יום</strong> — בשלב האחרון תוכל להגדיר שעה שונה לתפילה זו
          בימים ספציפיים בלבד. למשל: שחרית בכל הימים ב-7:30, אבל בשישי ב-7:00.
          כך לא צריך ליצור תפילה נפרדת לכל יום.
        </Typography>
      </Box>
    </Box>
  );
};

// ─── SubStep1–3 ───────────────────────────────────────────────────────────────

const PRAYER_TYPE_LABELS = PRAYER_TYPES.map(p => p.label);

const SubStep1 = ({ draft, setDraft }) => {
  const handleTypeClick = (p) => {
    setDraft(d => ({
      ...d,
      prayer: p.value,
      // auto-fill name only when it's still empty or matches a known prayer type name
      label: (!d.label || PRAYER_TYPE_LABELS.includes(d.label)) ? p.label : d.label,
    }));
  };

  return (
    <Stack spacing={2}>
      {/* Type buttons — first */}
      <Box>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 1 }}>
          סוג התפילה
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {PRAYER_TYPES.map(p => {
            const isSelected = draft.prayer === p.value;
            return (
              <Box
                key={p.value}
                onClick={() => handleTypeClick(p)}
                sx={{
                  px: 2, py: 0.8, borderRadius: '20px', cursor: 'pointer',
                  fontSize: '0.85rem', fontWeight: isSelected ? 700 : 500,
                  bgcolor: isSelected ? navy : 'white',
                  color: isSelected ? 'white' : '#374151',
                  border: `1.5px solid ${isSelected ? navy : '#e5e7eb'}`,
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: navy },
                }}
              >
                {p.label}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Name field — after type */}
      <TextField
        size="small"
        label="שם התפילה"
        placeholder="ניתן לשנות את השם..."
        value={draft.label}
        onChange={(e) => setDraft(d => ({ ...d, label: e.target.value }))}
        fullWidth
        sx={inputSx}
      />
    </Stack>
  );
};

const SubStep2 = ({ draft, setDraft }) => (
  <Stack spacing={1}>
    {TIME_TYPES.map(tt => (
      <Box key={tt.value}
        onClick={() => setDraft(d => ({ ...d, type: tt.value }))}
        sx={{
          p: 1.5, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s',
          border: `1.5px solid ${draft.type === tt.value ? navy : '#e5e7eb'}`,
          bgcolor: draft.type === tt.value ? '#f0f4fa' : 'white',
        }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.83rem', color: draft.type === tt.value ? navy : '#111827' }}>
          {tt.label}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.25 }}>{tt.desc}</Typography>
      </Box>
    ))}
  </Stack>
);

const _HEB_MONTH = m => ['','ניסן','אייר','סיון','תמוז','אב','אלול','תשרי','חשון','כסלו','טבת','שבט','אדר א׳','אדר ב׳'][m]||'';
const _HEB_DAY = d => ['','א׳','ב׳','ג׳','ד׳','ה׳','ו׳','ז׳','ח׳','ט׳','י׳','י"א','י"ב','י"ג','י"ד','ט"ו','ט"ז','י"ז','י"ח','י"ט','כ׳','כ"א','כ"ב','כ"ג','כ"ד','כ"ה','כ"ו','כ"ז','כ"ח','כ"ט','ל׳'][d]||String(d);

const SubStep3 = ({ draft, setDraft, mode, zmanimForDay, contextLabel, moadDays, existingPrayers = [] }) => {
  if (draft.type === 'fixed') {
    if (mode === 'moad') {
      const selectedPart = draft.moadPart || 'day';
      const firstDayIdx = draft.moadDayIndices?.[0] ?? 0;
      const firstDay = moadDays?.[firstDayIdx];
      const dayHebDate = firstDay?.hebrewDay
        ? `${_HEB_DAY(firstDay.hebrewDay)} ${_HEB_MONTH(firstDay.hebrewMonth)}`
        : (firstDay?.name || 'המועד');
      const moadPartOptions = [
        { val: 'eve', lbl: `בליל ${dayHebDate}`,  desc: 'מתרחשת בליל המועד' },
        { val: 'day', lbl: `ביום ${dayHebDate}`,  desc: 'מתרחשת בשעות היום' },
      ];
      return (
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600, mb: 1 }}>
              מתי מתרחשת התפילה?
            </Typography>
            <Stack direction="row" spacing={1}>
              {moadPartOptions.map(opt => {
                const sel = selectedPart === opt.val;
                return (
                  <Box key={opt.val} onClick={() => setDraft(d => ({ ...d, moadPart: opt.val }))}
                    sx={{
                      flex: 1, p: 1.5, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s',
                      border: `1.5px solid ${sel ? navy : '#e5e7eb'}`,
                      bgcolor: sel ? '#f0f4fa' : 'white',
                    }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.83rem', color: sel ? navy : '#111827' }}>
                      {opt.lbl}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.25 }}>{opt.desc}</Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
          <TextField size="small" label="שעה" type="time" value={draft.time || ''}
            onChange={(e) => setDraft(d => ({ ...d, time: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, maxWidth: 160 }} />
        </Stack>
      );
    }
    return (
      <TextField size="small" label="שעה" type="time" value={draft.time || ''}
        onChange={(e) => setDraft(d => ({ ...d, time: e.target.value }))}
        InputLabelProps={{ shrink: true }}
        sx={{ ...inputSx, maxWidth: 160 }} />
    );
  }
  if (draft.type === 'relative') {
    const usingPrayerRef = String(draft.baseTime || '').startsWith('ct:');
    const referencablePrayers = mode === 'shabbat'
      ? existingPrayers.filter(p => p.id && p.label && p.type !== 'recurring')
      : [];

    return (
      <Stack spacing={1.5}>
        {/* Toggle: זמן הלכתי / ייחוס לתפילה — רק בשבת עם תפילות קיימות */}
        {referencablePrayers.length > 0 && (
          <Stack direction="row" spacing={0.75}>
            {[
              { val: false, label: 'זמן הלכתי' },
              { val: true,  label: 'ייחוס לתפילה אחרת' },
            ].map(opt => {
              const sel = usingPrayerRef === opt.val;
              return (
                <Box key={String(opt.val)} onClick={() => {
                  if (opt.val && !usingPrayerRef) {
                    setDraft(d => ({ ...d, baseTime: `ct:${referencablePrayers[0].id}` }));
                  } else if (!opt.val && usingPrayerRef) {
                    setDraft(d => ({ ...d, baseTime: 'daySunset' }));
                  }
                }} sx={{
                  px: 1.5, py: 0.4, borderRadius: '20px', cursor: 'pointer', userSelect: 'none',
                  fontSize: '0.75rem', fontWeight: sel ? 700 : 400,
                  bgcolor: sel ? navy : 'white', color: sel ? 'white' : '#6b7280',
                  border: `1px solid ${sel ? navy : '#e5e7eb'}`, transition: 'all 0.15s',
                  '&:hover': { borderColor: navy, color: sel ? 'white' : navy },
                }}>
                  {opt.label}
                </Box>
              );
            })}
          </Stack>
        )}

        {/* בורר: תפילה קיימת או זמן הלכתי */}
        {usingPrayerRef ? (
          <Select size="small"
            value={draft.baseTime || ''}
            onChange={(e) => setDraft(d => ({ ...d, baseTime: e.target.value }))}
            sx={{ ...inputSx }}>
            {referencablePrayers.map(p => (
              <MenuItem key={p.id} value={`ct:${p.id}`} sx={{ fontSize: '0.8rem' }}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <ZmanSelect
            value={draft.baseTime || 'daySunset'}
            onChange={(key) => setDraft(d => ({ ...d, baseTime: key }))}
            keyPrefix="day"
            extraOptions={mode === 'moad' ? MOAD_EXTRA_ZMAN_OPTIONS : mode === 'shabbat' ? SHABBAT_EXTRA_ZMAN_OPTIONS : []}
            hiddenDirectKeys={mode === 'moad' ? ['sunset'] : []}
            zmanimForDay={zmanimForDay}
            contextLabel={contextLabel}
          />
        )}

        <Stack direction="row" spacing={1.5}>
          <TextField size="small" label="offset" type="number"
            value={draft.offset ?? 0}
            onChange={(e) => setDraft(d => ({ ...d, offset: Number(e.target.value) }))}
            InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>דק׳</Typography></InputAdornment> }}
            sx={{ width: 90, ...inputSx }} />
          <Select size="small"
            value={draft.roundTo ?? 0}
            onChange={(e) => setDraft(d => ({ ...d, roundTo: e.target.value }))}
            sx={{ ...inputSx, width: 110 }}>
            <MenuItem value={0} sx={{ fontSize: '0.82rem' }}>ללא עיגול</MenuItem>
            <MenuItem value={5} sx={{ fontSize: '0.82rem' }}>עיגול ל-5 דק׳</MenuItem>
            <MenuItem value={10} sx={{ fontSize: '0.82rem' }}>עיגול ל-10 דק׳</MenuItem>
          </Select>
        </Stack>
      </Stack>
    );
  }
  // recurring
  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600 }}>התחלה</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Select size="small" value={draft.startType || 'fixed'}
          onChange={(e) => setDraft(d => ({ ...d, startType: e.target.value }))}
          sx={{ ...inputSx, fontSize: '0.78rem', width: 110 }}>
          <MenuItem value="fixed" sx={{ fontSize: '0.78rem' }}>שעה קבועה</MenuItem>
          <MenuItem value="relative" sx={{ fontSize: '0.78rem' }}>יחסי</MenuItem>
        </Select>
        {(!draft.startType || draft.startType === 'fixed') ? (
          <TextField size="small" type="time" value={draft.startTime || ''}
            onChange={(e) => setDraft(d => ({ ...d, startTime: e.target.value }))}
            sx={{ ...inputSx, flex: 1 }} InputLabelProps={{ shrink: true }} />
        ) : (
          <>
            <ZmanSelect
              value={draft.startBaseTime || 'dayNetz'}
              onChange={(key) => setDraft(d => ({ ...d, startBaseTime: key }))}
              keyPrefix="day"
              sx={{ flex: 1, fontSize: '0.78rem' }}
            />
            <TextField size="small" type="number" label="offset"
              value={draft.startOffset ?? 0}
              onChange={(e) => setDraft(d => ({ ...d, startOffset: Number(e.target.value) }))}
              InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>דק׳</Typography></InputAdornment> }}
              sx={{ width: 80, flexShrink: 0, ...inputSx }} />
          </>
        )}
      </Stack>
      <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600 }}>סיום</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Select size="small" value={draft.endType || 'fixed'}
          onChange={(e) => setDraft(d => ({ ...d, endType: e.target.value }))}
          sx={{ ...inputSx, fontSize: '0.78rem', width: 110 }}>
          <MenuItem value="fixed" sx={{ fontSize: '0.78rem' }}>שעה קבועה</MenuItem>
          <MenuItem value="relative" sx={{ fontSize: '0.78rem' }}>יחסי</MenuItem>
        </Select>
        {(!draft.endType || draft.endType === 'fixed') ? (
          <TextField size="small" type="time" value={draft.endTime || ''}
            onChange={(e) => setDraft(d => ({ ...d, endTime: e.target.value }))}
            sx={{ ...inputSx, flex: 1 }} InputLabelProps={{ shrink: true }} />
        ) : (
          <>
            <ZmanSelect
              value={draft.endBaseTime || 'daySunset'}
              onChange={(key) => setDraft(d => ({ ...d, endBaseTime: key }))}
              keyPrefix="day"
              sx={{ flex: 1, fontSize: '0.78rem' }}
            />
            <TextField size="small" type="number" label="offset"
              value={draft.endOffset ?? 0}
              onChange={(e) => setDraft(d => ({ ...d, endOffset: Number(e.target.value) }))}
              InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>דק׳</Typography></InputAdornment> }}
              sx={{ width: 80, flexShrink: 0, ...inputSx }} />
          </>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontSize: '0.78rem', color: '#374151', flexShrink: 0 }}>כל</Typography>
        <TextField size="small" type="number" value={draft.interval || 30}
          onChange={(e) => setDraft(d => ({ ...d, interval: Number(e.target.value) }))}
          InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>דק׳</Typography></InputAdornment> }}
          sx={{ width: 90, ...inputSx }} />
      </Stack>
    </Stack>
  );
};

// ─── SubFlowSummary ────────────────────────────────────────────────────────────

const SubFlowSummary = ({ draft, subStep, mode, moadDays }) => {
  if (subStep === 0) return null;

  const items = [];

  // Step 0 — days
  if (mode === 'moad') {
    const indices = draft.moadDayIndices || [];
    const names = indices.map(i => moadDays[i]?.name || `יום ${i + 1}`);
    if (names.length > 0) items.push({ label: 'ימים', value: names.length <= 3 ? names.join(', ') : `${names.length} ימים` });
  } else if (mode === 'shabbat') {
    items.push({ label: 'יום', value: draft.day === 'friday' ? 'יום שישי' : 'שבת' });
  } else {
    const days = draft.days || [];
    let val;
    if (days.length === 7) val = 'כל הימים';
    else if (days.length === 6 && !days.includes('sat')) val = 'א׳–ו׳';
    else if (days.length === 5 && !days.includes('sat') && !days.includes('fri')) val = 'א׳–ה׳';
    else val = days.map(k => WEEKDAYS.find(d => d.key === k)?.label || k).join(' ');
    if (val) items.push({ label: 'ימים', value: val });
  }

  // Step 1 — name + type
  if (subStep > 1) {
    const typeLabel = PRAYER_TYPES.find(t => t.value === draft.prayer)?.label || '';
    const nameVal = draft.label && draft.label !== typeLabel
      ? `${typeLabel} — ${draft.label}`
      : typeLabel;
    if (nameVal) items.push({ label: 'תפילה', value: nameVal });
  }

  // Step 2 — time type
  if (subStep > 2) {
    const ttLabel = TIME_TYPES.find(t => t.value === draft.type)?.label || '';
    if (ttLabel) items.push({ label: 'סוג', value: ttLabel });
  }

  if (items.length === 0) return null;

  return (
    <Box sx={{ bgcolor: '#f0f4fa', border: '1px solid #dbeafe', borderRadius: 2, px: 1.5, py: 0.75, mb: 1.5 }}>
      <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
        {items.map((item, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={0.5}
            sx={{ bgcolor: 'white', border: '1px solid #bfdbfe', borderRadius: '20px', px: 1.25, py: 0.3 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#6b7280' }}>{item.label}:</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: navy, fontWeight: 700 }}>{item.value}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

// ─── Sub-flow container ────────────────────────────────────────────────────────

const EMPTY_DRAFT = () => ({
  id: uuidv4(), label: 'שחרית', prayer: 'shacharit',
  type: 'fixed', time: '', baseTime: 'dayNetz', offset: 0, roundTo: 0,
  startType: 'fixed', startTime: '', endType: 'fixed', endTime: '', interval: 30,
  days: ALL_WEEKDAYS, moadDayIndices: [], day: 'shabbat', moadPart: 'day',
});

// ─── SubStep5 — עקיפות ימים ───────────────────────────────────────────────────

const OVERRIDE_DAY_OPTIONS = [
  { value: 0, label: 'א׳' }, { value: 1, label: 'ב׳' }, { value: 2, label: 'ג׳' },
  { value: 3, label: 'ד׳' }, { value: 4, label: 'ה׳' }, { value: 5, label: 'ו׳' },
  { value: 'roshChodesh', label: 'ר"ח' },
];

const SubStep5 = ({ draft, setDraft, zmanimForDay, contextLabel }) => {
  const overrides = draft.dayOverrides || [];
  const addOverride = () => setDraft(d => ({ ...d, dayOverrides: [...(d.dayOverrides || []), { days: [], type: 'fixed', time: '', baseTime: 'dayNetz', offset: 0 }] }));
  const removeOverride = (i) => setDraft(d => ({ ...d, dayOverrides: (d.dayOverrides || []).filter((_, j) => j !== i) }));
  const updateOverride = (i, partial) => setDraft(d => {
    const ovs = [...(d.dayOverrides || [])];
    ovs[i] = { ...ovs[i], ...partial };
    return { ...d, dayOverrides: ovs };
  });

  return (
    <Stack spacing={1.5}>
      <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#0c4a6e', mb: 0.3 }}>עקיפה לפי יום — שלב אופציונלי</Typography>
        <Typography sx={{ fontSize: '0.73rem', color: '#0c4a6e', lineHeight: 1.6 }}>
          השעה שהגדרת חלה על כל הימים שבחרת. כאן תוכל להגדיר <strong>יוצא דופן</strong> — יום ספציפי שבו השעה שונה.
          למשל: שחרית בכל הימים ב-7:30, אבל בשישי ב-7:00. ניתן להוסיף מספר עקיפות לימים שונים, או לדלג לחלוטין.
        </Typography>
      </Box>

      {overrides.map((ov, i) => (
        <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'white', border: '1px solid #e5e7eb' }}>
          {/* Day chips */}
          <Stack direction="row" alignItems="center" spacing={0.5} mb={1} flexWrap="wrap" gap={0.5}>
            <Typography sx={{ fontSize: '0.72rem', color: '#374151', fontWeight: 600, flexShrink: 0 }}>ימים:</Typography>
            {OVERRIDE_DAY_OPTIONS.map((d, di) => {
              const sel = (ov.days || []).includes(d.value);
              const isSpecial = d.value === 'roshChodesh';
              return (
                <React.Fragment key={d.value}>
                  {isSpecial && <Box sx={{ width: 1, height: 16, bgcolor: '#e5e7eb', alignSelf: 'center', flexShrink: 0 }} />}
                  <Box onClick={() => {
                    const days = sel ? (ov.days||[]).filter(x=>x!==d.value) : [...(ov.days||[]),d.value];
                    updateOverride(i, { days });
                  }} sx={{
                    px: isSpecial ? 1.1 : 0.9, py: 0.2, borderRadius: 1.2, cursor: 'pointer', userSelect: 'none',
                    fontSize: '0.73rem', fontWeight: 700,
                    bgcolor: sel ? navy : 'white', color: sel ? 'white' : '#9ca3af',
                    border: sel ? `1.5px solid ${navy}` : '1px solid #e5e7eb',
                  }}>{d.label}</Box>
                </React.Fragment>
              );
            })}
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={() => removeOverride(i)} sx={{ color: '#d1d5db', '&:hover': { color: '#ef4444' } }}>
              <Trash2 size={14} />
            </IconButton>
          </Stack>

          {/* Type toggle */}
          <Stack direction="row" spacing={0.75} mb={1}>
            {[{ val: 'fixed', lbl: 'קבוע' }, { val: 'relative', lbl: 'יחסי' }].map(opt => {
              const sel = (ov.type || 'fixed') === opt.val;
              return (
                <Box key={opt.val} onClick={() => updateOverride(i, { type: opt.val })}
                  sx={{
                    px: 1.2, py: 0.3, borderRadius: 1.2, cursor: 'pointer', userSelect: 'none',
                    fontSize: '0.73rem', fontWeight: sel ? 600 : 400,
                    bgcolor: sel ? navy : 'white', color: sel ? 'white' : '#9ca3af',
                    border: sel ? `1px solid ${navy}` : '1px solid #e5e7eb', transition: 'all 0.15s',
                  }}>{opt.lbl}</Box>
              );
            })}
          </Stack>

          {/* Time value */}
          {ov.type === 'relative' ? (
            <Stack direction="row" spacing={1}>
              <ZmanSelect
                value={ov.baseTime || 'dayNetz'}
                onChange={(key) => updateOverride(i, { baseTime: key })}
                keyPrefix="day"
                zmanimForDay={zmanimForDay}
                contextLabel={contextLabel}
                sx={{ fontSize: '0.78rem' }}
              />
              <TextField size="small" type="number" value={ov.offset ?? 0}
                onChange={(e) => updateOverride(i, { offset: Number(e.target.value) })}
                InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>דק׳</Typography></InputAdornment> }}
                sx={{ width: 80, flexShrink: 0, ...inputSx }} />
            </Stack>
          ) : (
            <TextField size="small" type="time" value={ov.time || ''}
              onChange={(e) => updateOverride(i, { time: e.target.value })}
              InputLabelProps={{ shrink: true }} fullWidth sx={inputSx} />
          )}
        </Box>
      ))}

      <Button size="small" startIcon={<Plus size={13} />} onClick={addOverride}
        sx={{
          border: '1px dashed #d1d5db', borderRadius: '8px', textTransform: 'none',
          fontSize: '0.78rem', color: '#6b7280', px: 1.5, py: 0.75,
          '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
        }}>
        הוסף עקיפה ליום ספציפי
      </Button>
    </Stack>
  );
};

// סדר: ימים ← שם+סוג ← סוג זמן ← הגדרת הזמן ← עקיפות
const ALL_SUB_STEPS = ['ימים', 'שם וסוג', 'סוג זמן', 'הגדרת הזמן', 'עקיפות'];

const PrayerSubFlow = ({ onSave, onCancel, mode, moadDays, todayZmanim, onFetchZmanimForDay, initialDraft, isEditing, existingPrayers = [] }) => {
  // שבת ומועד אין צורך בעקיפות ימים
  const SUB_STEPS = (mode === 'shabbat' || mode === 'moad') ? ALL_SUB_STEPS.slice(0, 4) : ALL_SUB_STEPS;
  const [draft, setDraft] = useState(() => {
    if (!initialDraft) return EMPTY_DRAFT();
    const merged = { ...EMPTY_DRAFT(), ...initialDraft };
    // תפילות ישנות עלולות לא לכלול שדה type — נסיק מהנתונים
    if (!initialDraft.type) {
      if (initialDraft.offset || (initialDraft.baseTime && initialDraft.baseTime !== 'dayNetz')) {
        merged.type = 'relative';
      } else if (initialDraft.startTime || initialDraft.endTime) {
        merged.type = 'recurring';
      }
    }
    return merged;
  });
  const [subStep, setSubStep] = useState(0);
  const [moadDayZmanim, setMoadDayZmanim] = useState(null);
  const [moadDateLabel, setMoadDateLabel] = useState('');
  const firstSelectedDayIdx = mode === 'moad' ? (draft.moadDayIndices?.[0] ?? null) : null;

  useEffect(() => {
    if (mode !== 'moad' || firstSelectedDayIdx === null || !onFetchZmanimForDay) return;
    const day = moadDays?.[firstSelectedDayIdx];
    if (!day?.hebrewMonth || !day?.hebrewDay) return;
    onFetchZmanimForDay(day.hebrewMonth, day.hebrewDay)
      .then(z => { setMoadDayZmanim(z); setMoadDateLabel(day.name || `יום ${firstSelectedDayIdx + 1}`); })
      .catch(() => { setMoadDayZmanim(null); setMoadDateLabel(''); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSelectedDayIdx, mode]);

  const activeZmanim = (mode === 'moad' && moadDayZmanim) ? moadDayZmanim : todayZmanim;
  const activeDateLabel = (mode === 'moad' && moadDateLabel) ? moadDateLabel : null;

  const canAdvance = () => {
    if (subStep === 0) {
      if (mode === 'moad') return (draft.moadDayIndices || []).length > 0;
      if (mode === 'tefilot') return (draft.days || []).length > 0;
      return true; // shabbat — תמיד אפשר להמשיך
    }
    if (subStep === 1) return draft.label.trim().length > 0;
    if (subStep === 3 && draft.type === 'fixed') return draft.time.length > 0;
    return true;
  };

  const canSave = isEditing && draft.label.trim().length > 0 &&
    (draft.type !== 'fixed' || (draft.time && draft.time.length > 0));

  return (
    <Box sx={{ border: '1px solid #bae6fd', borderRadius: 2, bgcolor: '#f0f9ff', p: 2, mb: 1.5 }}>
      {/* Stepper tabs */}
      <Stack direction="row" spacing={0.5} alignItems="center" mb={2}>
        {SUB_STEPS.map((label, i) => {
          const isCurrent = i === subStep;
          const isCompleted = !isEditing && i < subStep;
          // בעריכה — כל הטאבים לחיצים; בהוספה — רק שלבים שהושלמו
          const isClickable = isEditing || isCompleted;
          return (
            <React.Fragment key={i}>
              <Box
                onClick={isClickable ? () => setSubStep(i) : undefined}
                sx={{
                  px: 1.25, py: 0.4, borderRadius: '20px', fontSize: '0.7rem',
                  fontWeight: isCurrent ? 700 : isEditing ? 600 : 400,
                  whiteSpace: 'nowrap',
                  bgcolor: isCurrent ? navy
                    : isEditing ? '#dbeafe'
                    : isCompleted ? '#dbeafe'
                    : 'transparent',
                  color: isCurrent ? 'white'
                    : isEditing ? '#1d4ed8'
                    : isCompleted ? '#1d4ed8'
                    : '#9ca3af',
                  border: `1px solid ${isCurrent ? navy : isEditing ? '#93c5fd' : '#e5e7eb'}`,
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  '&:hover': isClickable && !isCurrent ? { bgcolor: '#bfdbfe', borderColor: '#60a5fa' } : {},
                }}
              >
                {isCompleted ? '✓' : `${i + 1}. ${label}`}
              </Box>
              {i < SUB_STEPS.length - 1 && (
                <Box sx={{ flex: 1, height: 1, bgcolor: (isEditing || isCompleted) ? '#bae6fd' : '#e5e7eb' }} />
              )}
            </React.Fragment>
          );
        })}
      </Stack>

      {/* Progress summary — only for new prayer */}
      {!isEditing && <SubFlowSummary draft={draft} subStep={subStep} mode={mode} moadDays={moadDays} />}

      {/* Content */}
      <Box mb={2}>
        {subStep === 0 && <SubStep4 draft={draft} setDraft={setDraft} mode={mode} moadDays={moadDays} />}
        {subStep === 1 && <SubStep1 draft={draft} setDraft={setDraft} />}
        {subStep === 2 && <SubStep2 draft={draft} setDraft={setDraft} />}
        {subStep === 3 && <SubStep3 draft={draft} setDraft={setDraft} mode={mode} zmanimForDay={activeZmanim} contextLabel={activeDateLabel} moadDays={moadDays} existingPrayers={existingPrayers} />}
        {subStep === 4 && <SubStep5 draft={draft} setDraft={setDraft} zmanimForDay={activeZmanim} contextLabel={activeDateLabel} />}
      </Box>

      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          {/* ביטול — בעריכה תמיד מוצג, בהוספה רק בשלב 0 */}
          {(isEditing || subStep === 0) && (
            <Button onClick={onCancel}
              sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
              ביטול
            </Button>
          )}
          {subStep > 0 && (
            <Button onClick={() => setSubStep(s => s - 1)}
              sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
              חזור →
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* בעריכה — כפתור עדכון תמיד זמין */}
          {isEditing && (
            <Button variant="contained"
              onClick={() => onSave(draft)}
              disabled={!canSave}
              sx={{
                bgcolor: '#16a34a', color: 'white', textTransform: 'none', fontSize: '0.8rem',
                boxShadow: 'none', borderRadius: '8px',
                '&:hover': { bgcolor: '#15803d', boxShadow: 'none' },
              }}>
              עדכן תפילה
            </Button>
          )}

          {/* כפתור המשך / הוסף */}
          {subStep < SUB_STEPS.length - 1 ? (
            <Button variant={isEditing ? 'outlined' : 'contained'}
              onClick={() => setSubStep(s => s + 1)}
              disabled={!canAdvance()}
              sx={{
                bgcolor: isEditing ? 'transparent' : navy,
                color: isEditing ? navy : 'white',
                borderColor: isEditing ? navy : undefined,
                textTransform: 'none', fontSize: '0.8rem',
                boxShadow: 'none', borderRadius: '8px',
                '&:hover': { bgcolor: isEditing ? '#f0f4fa' : '#152844', boxShadow: 'none' },
              }}>
              ← המשך
            </Button>
          ) : !isEditing && (
            <Button variant="contained"
              onClick={() => onSave(draft)}
              disabled={!canAdvance()}
              sx={{
                bgcolor: navy, color: 'white', textTransform: 'none', fontSize: '0.8rem',
                boxShadow: 'none', borderRadius: '8px',
                '&:hover': { bgcolor: '#152844', boxShadow: 'none' },
              }}>
              הוסף תפילה
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

// ─── Prayer summary row ────────────────────────────────────────────────────────

const daysSummary = (prayer, mode, moadDays) => {
  if (mode === 'shabbat') return (prayer.day || 'shabbat') === 'friday' ? 'יום שישי' : 'שבת';
  if (mode === 'moad' && moadDays?.length > 0) {
    const indices = prayer.moadDayIndices ?? moadDays.map((_, i) => i);
    if (indices.length === moadDays.length) return 'כל ימי המועד';
    return indices.map(i => moadDays[i]?.name || `יום ${i + 1}`).join(', ');
  }
  const days = prayer.days || ALL_WEEKDAYS;
  if (days.length === 7) return 'כל הימים';
  if (days.length === 6 && !days.includes('sat')) return 'א׳–ו׳';
  if (days.length === 5 && !days.includes('sat') && !days.includes('fri')) return 'א׳–ה׳';
  return days.map(k => WEEKDAYS.find(d => d.key === k)?.label || k).join(' ');
};

const ALL_EXTRA_ZMAN_OPTIONS = [...SHABBAT_EXTRA_ZMAN_OPTIONS, ...MOAD_EXTRA_ZMAN_OPTIONS];

const prayerTimeSummary = (p) => {
  const typeLabel = PRAYER_TYPES.find(t => t.value === p.prayer)?.label || '';
  // תפילות ישנות ללא type — נסיק
  const pType = p.type || (p.offset || (p.baseTime && p.baseTime !== 'dayNetz') ? 'relative' : p.startTime ? 'recurring' : 'fixed');
  let time = '';
  if (pType === 'fixed') time = p.time;
  else if (pType === 'relative') {
    const baseTimeStr = p.baseTime || '';
    let base;
    if (baseTimeStr.startsWith('ct:')) {
      base = 'ייחוס לתפילה אחרת';
    } else {
      const zmanLabel = getZmanLabel(baseTimeStr);
      base = (zmanLabel && zmanLabel !== baseTimeStr) ? zmanLabel
        : ALL_EXTRA_ZMAN_OPTIONS.find(o => o.key === baseTimeStr)?.label
        || baseTimeStr;
    }
    time = p.offset !== 0 ? `${base} ${p.offset > 0 ? '+' : ''}${p.offset} דק'` : base;
  } else {
    time = `${p.startTime || '?'}–${p.endTime || '?'} כל ${p.interval} דק'`;
  }
  return { typeLabel, time };
};

// ─── Main component ────────────────────────────────────────────────────────────

const PrayerListStep = ({ mode = 'tefilot', formData, onChange, onSubflowChange, todayZmanim, onFetchZmanimForDay }) => {
  const fieldKey  = FIELD_MAP[mode] || 'customTimes';
  const prayers   = formData?.[fieldKey] || [];
  const moadDays  = formData?.days || [];
  const [adding, setAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const openSubflow  = () => { setEditingIndex(null); setAdding(true);  onSubflowChange?.(true);  };
  const closeSubflow = () => { setAdding(false); setEditingIndex(null); onSubflowChange?.(false); };

  const startEdit = (index) => {
    setEditingIndex(index);
    setAdding(true);
    onSubflowChange?.(true);
  };

  const updatePrayers = (updated) => onChange({ [fieldKey]: updated });
  const removePrayer  = (index)   => {
    updatePrayers(prayers.filter((_, i) => i !== index));
    if (editingIndex === index) closeSubflow();
  };
  const savePrayer = (draft) => {
    // זמנים יחסיים לא צריכים moadPart — מונע זיהום הלוגיקה ב-Dashboard
    const cleaned = (mode === 'moad' && draft.type !== 'fixed')
      ? (({ moadPart, ...rest }) => rest)(draft)
      : draft;

    if (editingIndex !== null) {
      // עדכון תפילה קיימת
      updatePrayers(prayers.map((p, i) => i === editingIndex ? { ...cleaned, id: p.id || cleaned.id } : p));
    } else if (mode === 'moad' && cleaned.moadDayIndices?.length > 0) {
      // במועד: פרוס כל יום לפריט נפרד עם dayIndex יחיד (כנדרש ע"י MoadWidget)
      const expanded = cleaned.moadDayIndices.map(idx => ({
        ...cleaned, id: uuidv4(), dayIndex: idx,
      }));
      updatePrayers([...prayers, ...expanded]);
    } else {
      updatePrayers([...prayers, { ...cleaned }]);
    }
    closeSubflow();
  };

  const modeLabel = mode === 'tefilot' ? 'תפילות יום חול' : mode === 'shabbat' ? 'תפילות שבת' : 'תפילות מועד';

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        {modeLabel}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2, lineHeight: 1.5 }}>
        הוסף תפילות עם שמות, שעות וימים. כל תפילה תציג את שעתה המחושבת בתצוגה.
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Right side (RTL): prayer list */}
        <Box sx={{ flex: adding ? '0 0 40%' : 1, minWidth: 0, transition: 'flex 0.2s' }}>
          <Button startIcon={<Plus size={15} />} onClick={openSubflow} disabled={adding}
            sx={{
              border: '1.5px dashed #d1d5db', borderRadius: '10px',
              color: '#6b7280', textTransform: 'none', fontSize: '0.8rem',
              px: 2, py: 0.75, width: '100%', mb: 1.5,
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
              '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#c0c0c0' },
            }}>
            הוסף תפילה
          </Button>

          {prayers.length > 0 ? (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflowY: 'auto', maxHeight: 'calc(70vh - 160px)' }}>
              {prayers.map((prayer, i) => {
                const { typeLabel, time } = prayerTimeSummary(prayer);
                const days = daysSummary(prayer, mode, moadDays);
                const isActive = editingIndex === i;
                return (
                  <Box key={prayer.id || i} sx={{
                    px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 0.75,
                    bgcolor: isActive ? '#eff6ff' : i % 2 === 0 ? '#fafafa' : 'white',
                    borderBottom: i < prayers.length - 1 ? '1px solid #f0f0f0' : 'none',
                    borderRight: isActive ? `3px solid ${navy}` : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}>
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827' }}>
                        {prayer.label}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <Typography noWrap sx={{ fontSize: '0.72rem', color: '#6b7280' }}>
                          {typeLabel} · {time}
                        </Typography>
                        <Box sx={{
                          px: 0.75, py: 0.1, borderRadius: '10px',
                          bgcolor: '#f0f4fa', border: '1px solid #dbeafe',
                          fontSize: '0.65rem', color: navy, fontWeight: 600, whiteSpace: 'nowrap',
                        }}>
                          {days}
                        </Box>
                      </Stack>
                    </Stack>
                    <IconButton size="small" onClick={() => startEdit(i)}
                      sx={{ color: isActive ? navy : '#9ca3af', '&:hover': { color: navy }, p: 0.5 }}>
                      <Pencil size={13} />
                    </IconButton>
                    <IconButton size="small" onClick={() => removePrayer(i)}
                      sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' }, p: 0.5 }}>
                      <Trash2 size={13} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          ) : !adding && (
            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#9ca3af', mt: 1 }}>
              אין תפילות — ניתן לדלג על שלב זה
            </Typography>
          )}
        </Box>

        {/* Left side (RTL): subflow form */}
        {adding && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PrayerSubFlow
              key={editingIndex ?? 'new'}
              onSave={savePrayer}
              onCancel={closeSubflow}
              mode={mode}
              moadDays={moadDays}
              todayZmanim={todayZmanim}
              onFetchZmanimForDay={onFetchZmanimForDay}
              initialDraft={editingIndex !== null ? prayers[editingIndex] : null}
              isEditing={editingIndex !== null}
              existingPrayers={editingIndex !== null
                ? prayers.filter((_, i) => i !== editingIndex)
                : prayers}
            />
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default PrayerListStep;
